// ./src/components/generic/MediaSelector/tabs/GalleryTab.tsx
//
// "Gallery" — the brand's ready-made pictures (owner, 2026-10-29: the legacy PartyLabz catalog of cover pictures, so a
// host moving over keeps picking from it). They are ms_storage STANDARD files — ordinary uploaded files under the nil
// account of the brand's space, one shared copy that every event which picks it points at — so a pick is a stored file
// like a library pick, and nothing is uploaded per event.
//
// The set is read WHOLE by the MediaSelector (a picker's options — ~200 pictures) and handed in, which is also how the
// selector knows whether to offer this tab at all. Folders (`filePath`) are the categories; the search and the folder
// narrow the loaded set here, which is right because it IS the whole set.
//
// ⚠ Branch order: not read yet → read failed → nothing matches → the grid.

import React, { useMemo, useState } from "react";
import { Box, Center, Chip, Group, Loader, ScrollArea, SimpleGrid, Stack, Text, TextInput } from "@mantine/core";
import { OpResult } from "@sdflc/api-helpers";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { UploadedFile } from "@/utils/api/UploadedFileService";

import { AppIcon } from "../../AppIcon";
import { ErrorAlert } from "../../ErrorAlert";
import { SelectableFileCard } from "../../SelectableFileCard";

/** Names for the legacy categories that a plain "capitalise the code" would get wrong. */
const FOLDER_LABELS: Record<string, string> = {
  baby: "Babies",
  "class-reunion": "Class reunion",
  "fathers-day": "Father's Day",
  other: "Other",
};

const humanize = (code: string): string => {
  const words = code.replace(/[-_]+/g, " ").trim();

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code;
};

/** A folder's label — translated when the locale has it. */
const folderLabel = (t: TFunction, folder: string): string =>
  String(t(`common:mediaSelector.galleryFolders.${folder}`, { defaultValue: FOLDER_LABELS[folder] ?? humanize(folder) }));

/** A picture's caption: its stored title (the legacy name), else its file name without the extension. */
export const galleryCaption = (file: UploadedFile): string =>
  (file.notes ?? "").trim() || humanize(String(file.name ?? file.originalFilename ?? "").replace(/\.[a-z0-9]+$/i, ""));

export interface GalleryTabProps {
  /** The brand's standard files for this kind and purpose — the WHOLE set. */
  files: UploadedFile[];
  /** Whether that read has come back. */
  isLoaded: boolean;
  /** Why it failed, when it did. */
  error: string | null;
  /** Shared search text (persists across tabs). */
  query: string;
  onQueryChange: (q: string) => void;
  /** Currently selected file id (single-select highlight). */
  selectedFileId: string | null;
  /** Multi-select: highlight every id in this set + toggle on click. */
  multiSelect?: boolean;
  selectedFileIds?: string[];
  /** Called when the selection changes (null when deselected). */
  onPick: (file: UploadedFile | null) => void;
}

export const GalleryTab: React.FC<GalleryTabProps> = ({
  files,
  isLoaded,
  error,
  query,
  onQueryChange,
  selectedFileId,
  multiSelect = false,
  selectedFileIds = [],
  onPick,
}) => {
  const { t } = useTranslation(["common"]);
  const [folder, setFolder] = useState<string>("");

  // The folders, in the order the set came in (by folder, then the order inside it).
  const folders = useMemo(() => [...new Set(files.map((f) => f.filePath ?? "").filter(Boolean))], [files]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return files.filter((f) => {
      if (folder && f.filePath !== folder) {
        return false;
      }

      if (!needle) {
        return true;
      }

      return (
        galleryCaption(f).toLowerCase().includes(needle) ||
        folderLabel(t, f.filePath ?? "")
          .toLowerCase()
          .includes(needle)
      );
    });
  }, [files, folder, query, t]);

  const errorResult = useMemo(() => (error ? new OpResult().addError("", error) : null), [error]);

  const handleSelect = (fileId: string, selected: boolean) => {
    const file = files.find((f) => f.id === fileId) || null;

    // Multi-select: every click emits the file so the parent toggles it.
    if (multiSelect) {
      if (file) {
        onPick(file);
      }

      return;
    }

    onPick(selected ? file : null);
  };

  return (
    <Stack gap="sm" h="100%">
      <TextInput
        size="md"
        placeholder={t("common:mediaSelector.searchGallery", { defaultValue: "Search the gallery…" })}
        value={query}
        onChange={(e) => onQueryChange(e.currentTarget.value)}
        leftSection={<AppIcon iconName="Search" size={16} />}
      />

      {/* One scrolling row: ~20 categories would otherwise fill a phone's screen before the first picture. */}
      {folders.length > 1 && (
        <ScrollArea type="auto" scrollbarSize={6} offsetScrollbars="x">
          <Chip.Group multiple={false} value={folder} onChange={(value) => setFolder((value as string) || "")}>
            <Group gap="xs" wrap="nowrap">
              <Chip value="" size="md" variant="light">
                {t("common:mediaSelector.galleryAll", { defaultValue: "All" })}
              </Chip>
              {folders.map((code) => (
                <Chip key={code} value={code} size="md" variant="light">
                  {folderLabel(t, code)}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </ScrollArea>
      )}

      {!isLoaded && (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      )}

      {isLoaded && errorResult && (
        <ErrorAlert
          error={errorResult}
          title={t("common:mediaSelector.galleryFailed", { defaultValue: "The gallery could not be loaded" })}
        />
      )}

      {isLoaded && !errorResult && shown.length === 0 && (
        <Center py="xl">
          <Box ta="center">
            <AppIcon iconName="Photo" size={44} style={{ color: "var(--mantine-color-gray-4)" }} />
            <Text size="md" c="dimmed" mt="sm">
              {query.trim() || folder
                ? t("common:mediaSelector.galleryNoMatch", { defaultValue: "No pictures match your search." })
                : t("common:mediaSelector.galleryEmpty", {
                    defaultValue: "There are no ready-made pictures here yet.",
                  })}
            </Text>
          </Box>
        </Center>
      )}

      {isLoaded && !errorResult && shown.length > 0 && (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
          {shown.map((file) => (
            <SelectableFileCard
              key={file.id}
              file={file}
              caption={galleryCaption(file)}
              selected={multiSelect ? selectedFileIds.includes(file.id ?? "") : file.id === selectedFileId}
              onSelect={handleSelect}
              // ⚠ The card's fixed 180px is wider than a phone's two columns (~165px): the tiles ran into each other.
              fullWidth
            />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
};
